import React, { useState } from "react";
import {
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
} from "@mui/material";
import {
  Logout,
  AccountCircle as AccountCircleIcon,
  ReceiptLong as ReceiptLongIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSnackbarAlert } from "../../hooks/useSnackbarAlert";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import api from "../../services/api";

const AccountMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const snackbar = useSnackbarAlert();
  const navigate = useNavigate();

  const { user, isAuthenticated, handleLogout } = useAuth();

  if (!isAuthenticated || !user) return null;

  const role = user?.employee?.role ?? user?.profile?.role ?? null;

  const isAdmin = ["admin", "manager", "staff"].includes(role);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  const handleLogoutBtnClick = async () => {
    try {
      const firebaseUser = auth.currentUser;

      if (firebaseUser) {
        const token = await firebaseUser.getIdToken();

        await api.post("/auth/logout", null, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await signOut(auth);
      handleLogout();
      snackbar.info("You logged out successfully. See you soon!");
    } catch (error) {
      console.error("Logout failed", error);
      snackbar.error("Failed to log out. Please try again.");
    }
  };

  const firstInitial = user?.profile?.firstName?.[0]?.toUpperCase() || "U";

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{ ml: 1 }}
        aria-controls={anchorEl ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}>
        <Avatar sx={{ width: 36, height: 36 }}>{firstInitial}</Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1.5,
            minWidth: 180,
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
        {isAdmin && (
          <MenuItem onClick={() => handleNavigate("/admin/dashboard")}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            Admin Dashboard
          </MenuItem>
        )}

        <MenuItem onClick={() => handleNavigate("/account")}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          My Account
        </MenuItem>

        <MenuItem onClick={() => handleNavigate("/order-history")}>
          <ListItemIcon>
            <ReceiptLongIcon fontSize="small" />
          </ListItemIcon>
          My Orders
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogoutBtnClick}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Log Out
        </MenuItem>
      </Menu>
    </>
  );
};

export default AccountMenu;
